'use client';

/**
 * Architecture Flow Widget (Pixi.js WebGL)
 *
 * @component_role Infographic Flow Visualization with Edge Animation
 * @business_rule Renders system topology as a narrative journey with animated
 *   flow currents. Designed for executive audiences - "the story" not "the plumbing".
 *
 * Edge Animation Types:
 * - DATA_FLOW: Slow, steady slate particles
 * - ACTION_EXECUTION: Sharp, fast orange sparks
 * - CONTEXT_ENRICHMENT: Cyan flow pulses
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain docs
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { api } from '@/lib/api/client';
import { Loader2, Network } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// =============================================================================
// TYPES
// =============================================================================

interface CanvasNodeLayout {
  column: number;
  row: number;
}

interface ArchNode {
  id: string;
  label: string;
  type: string;
  description?: string;
  icon?: string;
  marketing_name?: string;
  semantic_id?: string;
  synthetic?: boolean;
  layout?: CanvasNodeLayout;
  extra?: Record<string, unknown>;
}

interface ArchEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  label?: string;
}

interface FlowEdgeStyle {
  color: string;
  stroke_width: number;
  stroke_dasharray?: string;
  label: string;
  description: string;
}

interface ArchData {
  nodes: ArchNode[];
  edges: ArchEdge[];
  metadata: {
    node_count: number;
    edge_count: number;
    packages: number;
    capabilities: number;
    synthetic_count: number;
  };
  flow_edge_styles?: Record<string, FlowEdgeStyle>;
}

// =============================================================================
// THEME & COLORS
// =============================================================================

const THEME = {
  dark: {
    background: 0x0a0a0f,
    nodeBackground: 0x1a1a2e,
    nodeBorder: 0x2a2a3e,
    hubGlow: 0x22d3ee, // cyan-400
    textPrimary: 0xffffff,
    textSecondary: 0xaaaaaa,
    textMuted: 0x666666,
  },
  light: {
    background: 0xf8fafc,
    nodeBackground: 0xffffff,
    nodeBorder: 0xe2e8f0,
    hubGlow: 0x0891b2, // cyan-600
    textPrimary: 0x1e293b,
    textSecondary: 0x475569,
    textMuted: 0x94a3b8,
  },
};

// Edge animation colors (hex number format for Pixi)
// DATA_FLOW is intentionally muted - boring on purpose for contrast
const EDGE_COLORS: Record<string, { color: number; speed: number; size: number; alpha: number }> = {
  DATA_FLOW: { color: 0x94a3b8, speed: 0.25, size: 3, alpha: 0.4 },           // muted, slow, small
  ACTION_EXECUTION: { color: 0xf59e0b, speed: 1.4, size: 7, alpha: 1.0 },     // exciting, fast, large
  CONTEXT_ENRICHMENT: { color: 0x22d3ee, speed: 0.7, size: 5, alpha: 0.85 }, // intelligent, medium
  STATUS_FEEDBACK: { color: 0x10b981, speed: 0.4, size: 4, alpha: 0.6 },     // steady
  HUMAN_LOOP: { color: 0xa855f7, speed: 0.5, size: 5, alpha: 0.7 },          // medium
};

// Zone colors (RGBA for Pixi)
const ZONES = [
  { label: 'SOURCE', startCol: 0, endCol: 1, color: 0x94a3b8, alpha: 0.03 },
  { label: 'BRIDGE', startCol: 2, endCol: 2, color: 0xfbbf24, alpha: 0.02 },
  { label: 'BRAIN', startCol: 3, endCol: 4, color: 0x22d3ee, alpha: 0.03 },
  { label: 'ACTION', startCol: 5, endCol: 6, color: 0xa855f7, alpha: 0.02 },
];

// =============================================================================
// PARTICLE SYSTEM FOR EDGE ANIMATION
// =============================================================================

interface EdgeParticle {
  edgeId: string;
  progress: number; // 0 to 1 along edge
  speed: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  color: number;
  size: number;
  alpha: number;
  baseAlpha: number; // max alpha for this edge type
}

class EdgeAnimator {
  private particles: EdgeParticle[] = [];
  private edges: Array<{
    id: string;
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    type: string;
  }> = [];
  private spawnTimer = 0;
  private spawnInterval = 60; // frames between spawns

  setEdges(edges: Array<{
    id: string;
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    type: string;
  }>) {
    this.edges = edges;
    this.particles = [];
  }

  update(deltaTime: number) {
    // Update existing particles
    this.particles = this.particles.filter((p) => {
      p.progress += p.speed * deltaTime * 0.016; // normalize to ~60fps

      // Fade in/out at edges, scaled by baseAlpha
      if (p.progress < 0.1) {
        p.alpha = p.progress * 10 * p.baseAlpha;
      } else if (p.progress > 0.9) {
        p.alpha = (1 - p.progress) * 10 * p.baseAlpha;
      } else {
        p.alpha = p.baseAlpha;
      }

      return p.progress < 1;
    });

    // Spawn new particles
    this.spawnTimer++;
    if (this.spawnTimer >= this.spawnInterval) {
      this.spawnTimer = 0;
      this.spawnParticles();
    }
  }

  private spawnParticles() {
    // Spawn one particle per edge (staggered)
    const edgeIndex = Math.floor(Math.random() * this.edges.length);
    const edge = this.edges[edgeIndex];
    if (!edge) return;

    const style = EDGE_COLORS[edge.type] || EDGE_COLORS.DATA_FLOW;

    this.particles.push({
      edgeId: edge.id,
      progress: 0,
      speed: style.speed * (0.8 + Math.random() * 0.4), // slight variation
      sourceX: edge.sourceX,
      sourceY: edge.sourceY,
      targetX: edge.targetX,
      targetY: edge.targetY,
      color: style.color,
      size: style.size,
      alpha: 0,
      baseAlpha: style.alpha,
    });
  }

  // Trigger a burst of sparks on a specific edge (for action events)
  triggerSpark(edgeId: string) {
    const edge = this.edges.find((e) => e.id === edgeId);
    if (!edge) return;

    const style = EDGE_COLORS.ACTION_EXECUTION;

    // Spawn 5 rapid sparks - full alpha for impact
    for (let i = 0; i < 5; i++) {
      this.particles.push({
        edgeId: edge.id,
        progress: i * 0.05, // stagger
        speed: style.speed * 1.5,
        sourceX: edge.sourceX,
        sourceY: edge.sourceY,
        targetX: edge.targetX,
        targetY: edge.targetY,
        color: style.color,
        size: style.size * 1.5,
        alpha: 1,
        baseAlpha: 1.0, // sparks are always bright
      });
    }
  }

  getParticles(): EdgeParticle[] {
    return this.particles;
  }

  // Calculate current position of particle
  getParticlePosition(p: EdgeParticle): { x: number; y: number } {
    const x = p.sourceX + (p.targetX - p.sourceX) * p.progress;
    const y = p.sourceY + (p.targetY - p.sourceY) * p.progress;
    return { x, y };
  }
}

// =============================================================================
// LAYOUT CALCULATION
// =============================================================================

interface LayoutNode {
  id: string;
  label: string;
  marketingName?: string;
  semanticId?: string;
  type: string;
  description?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isHub: boolean;
  column: number;
  row: number;
  staggerY: number; // organic vertical offset
  extra?: Record<string, unknown>; // additional properties like parent_name
}

interface LayoutEdge {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  type: string;
  color: number;
}

function calculateLayout(
  data: ArchData,
  canvasWidth: number,
  canvasHeight: number
): { nodes: LayoutNode[]; edges: LayoutEdge[]; zones: typeof ZONES; scale: number } {
  const nodes: LayoutNode[] = [];
  const nodePositions = new Map<string, { x: number; y: number }>();

  // Find grid bounds
  const maxCol = Math.max(...data.nodes.map((n) => n.layout?.column ?? 0));
  const maxRow = Math.max(...data.nodes.map((n) => n.layout?.row ?? 0));
  const numCols = maxCol + 1;
  const numRows = maxRow + 1;

  // Base node sizes (before scaling) - 15% smaller
  const baseNodeWidth = 153;  // was 180
  const baseNodeHeight = 68;  // was 80
  const baseHubWidth = 187;   // was 220
  const baseHubHeight = 85;   // was 100

  // Calculate available space with padding
  const padding = 60; // Padding on all sides
  const availableWidth = canvasWidth - padding * 2;
  const availableHeight = canvasHeight - padding * 2;

  // Calculate ideal grid spacing
  const idealColWidth = availableWidth / numCols;
  const idealRowHeight = availableHeight / numRows;

  // Use the spacing that fits, with minimum sizes
  const colWidth = Math.max(idealColWidth, 160);
  const rowHeight = Math.max(idealRowHeight, 100);

  // Calculate scale factor if grid is larger than canvas
  const gridWidth = numCols * colWidth;
  const gridHeight = numRows * rowHeight;
  const scaleX = availableWidth / gridWidth;
  const scaleY = availableHeight / gridHeight;
  const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

  // Scaled dimensions
  const scaledColWidth = colWidth * scale;
  const scaledRowHeight = rowHeight * scale;
  const nodeWidth = baseNodeWidth * scale;
  const nodeHeight = baseNodeHeight * scale;
  const hubWidth = baseHubWidth * scale;
  const hubHeight = baseHubHeight * scale;

  // Calculate centering offset (center the scaled grid)
  const scaledGridWidth = numCols * scaledColWidth;
  const scaledGridHeight = numRows * scaledRowHeight;
  const offsetX = (canvasWidth - scaledGridWidth) / 2 + scaledColWidth / 2;
  const offsetY = (canvasHeight - scaledGridHeight) / 2 + scaledRowHeight / 2;

  // Stagger seed for deterministic but organic feel
  const staggerSeed = (label: string) => {
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = ((hash << 5) - hash) + label.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  };

  // Position nodes
  data.nodes.forEach((node) => {
    const col = node.layout?.column ?? 0;
    const row = node.layout?.row ?? 0;
    const isHub = node.label === 'klyde_edge';
    const w = isHub ? hubWidth : nodeWidth;
    const h = isHub ? hubHeight : nodeHeight;

    // Calculate organic stagger for capabilities and connectors (±8-12px)
    // Hub and core packages stay perfectly aligned
    let staggerY = 0;
    if (node.type === 'capability' || node.type === 'connector') {
      const seed = staggerSeed(node.label);
      staggerY = ((seed % 24) - 12) * scale; // ±12px scaled
    }

    const x = offsetX + col * scaledColWidth;
    const y = offsetY + row * scaledRowHeight + staggerY;

    nodePositions.set(node.label, { x, y });

    nodes.push({
      id: node.id,
      label: node.label,
      marketingName: node.marketing_name,
      semanticId: node.semantic_id,
      type: node.type,
      description: node.description,
      x,
      y,
      width: w,
      height: h,
      isHub,
      column: col,
      row: row,
      staggerY,
      extra: node.extra,
    });
  });

  // Calculate edges
  const edges: LayoutEdge[] = [];
  data.edges.forEach((edge) => {
    const source = nodePositions.get(edge.source);
    const target = nodePositions.get(edge.target);
    if (!source || !target) return;

    const style = EDGE_COLORS[edge.type] || EDGE_COLORS.DATA_FLOW;

    edges.push({
      id: edge.id,
      sourceX: source.x,
      sourceY: source.y,
      targetX: target.x,
      targetY: target.y,
      type: edge.type,
      color: style.color,
    });
  });

  return { nodes, edges, zones: ZONES, scale };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ArchFlowPixi() {
  const containerRef = useRef<HTMLDivElement>(null);
  const pixiContainerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<any>(null);
  const pixiModuleRef = useRef<any>(null);
  const animatorRef = useRef<EdgeAnimator>(new EdgeAnimator());
  const particleGraphicsRef = useRef<any>(null);

  const [data, setData] = useState<ArchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pixiReady, setPixiReady] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [containerMountedForResize, setContainerMountedForResize] = useState(false);
  const [pixiContainerMounted, setPixiContainerMounted] = useState(false);
  const [revealPhase, setRevealPhase] = useState<'dimmed' | 'revealing' | 'complete'>('dimmed');

  const { resolvedTheme } = useTheme();
  const colors = resolvedTheme === 'light' ? THEME.light : THEME.dark;
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Callback ref for outer container (resize tracking)
  const containerCallback = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node) setContainerMountedForResize(true);
  }, []);

  // Callback ref for pixi container (canvas mounting)
  const pixiContainerCallback = useCallback((node: HTMLDivElement | null) => {
    pixiContainerRef.current = node;
    if (node) setPixiContainerMounted(true);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await api.get<ArchData>('/docs/arch/flow');
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Track dimensions from container via ResizeObserver
  useEffect(() => {
    if (!containerMountedForResize || !containerRef.current) return;

    const update = () => {
      const { clientWidth, clientHeight } = containerRef.current!;
      if (clientWidth > 0 && clientHeight > 0) {
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    };
    update();

    const obs = new ResizeObserver(() => update());
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [containerMountedForResize]);

  // Initialize Pixi
  useEffect(() => {
    if (!pixiContainerMounted || appRef.current || !pixiContainerRef.current) return;

    let destroyed = false;

    const init = async () => {
      try {
        const PIXI = await import('pixi.js');
        if (destroyed) return;

        pixiModuleRef.current = PIXI;

        const app = new PIXI.Application();
        await app.init({
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: colors.background,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (destroyed) {
          app.destroy(true);
          return;
        }

        pixiContainerRef.current?.appendChild(app.canvas);
        appRef.current = app;
        setPixiReady(true);

      } catch (err) {
        console.error('Pixi init error:', err);
      }
    };

    init();

    return () => {
      destroyed = true;
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, [pixiContainerMounted]);

  // Resize handler - update renderer and hit area
  useEffect(() => {
    if (!pixiReady || !appRef.current || !pixiModuleRef.current) return;
    const PIXI = pixiModuleRef.current;
    const { width, height } = dimensions;

    // Resize the WebGL renderer
    appRef.current.renderer.resize(width, height);

    // Update hit area so interactions work at edges
    appRef.current.stage.hitArea = new PIXI.Rectangle(0, 0, width, height);
  }, [dimensions, pixiReady]);

  // Update background on theme change
  useEffect(() => {
    if (!pixiReady || !appRef.current) return;
    appRef.current.renderer.background.color = colors.background;
  }, [colors.background, pixiReady]);

  // Layout calculation
  const layout = useMemo(() => {
    if (!data || dimensions.width === 0) return null;
    return calculateLayout(data, dimensions.width, dimensions.height);
  }, [data, dimensions]);

  // Helper to find connected nodes for status narration
  const getNodePath = useCallback((nodeLabel: string): string => {
    if (!data) return nodeLabel;

    // Find incoming and outgoing connections
    const incoming = data.edges
      .filter(e => e.target === nodeLabel)
      .map(e => e.source);
    const outgoing = data.edges
      .filter(e => e.source === nodeLabel)
      .map(e => e.target);

    // Build path string
    if (incoming.length > 0 && outgoing.length > 0) {
      const inLabel = incoming[0];
      const outLabel = outgoing[0];
      return `${inLabel} → ${nodeLabel} → ${outLabel}`;
    } else if (outgoing.length > 0) {
      return `${nodeLabel} → ${outgoing[0]}`;
    } else if (incoming.length > 0) {
      return `${incoming[0]} → ${nodeLabel}`;
    }
    return nodeLabel;
  }, [data]);

  // Reveal animation timing
  useEffect(() => {
    if (!pixiReady || !layout) return;

    // Start dimmed, reveal after 300ms
    setRevealPhase('dimmed');
    const revealTimer = setTimeout(() => {
      setRevealPhase('revealing');
      // Complete after animation
      setTimeout(() => setRevealPhase('complete'), 600);
    }, 300);

    return () => clearTimeout(revealTimer);
  }, [pixiReady, layout]);

  // Render graph and animation loop
  useEffect(() => {
    if (!pixiReady || !layout || !appRef.current || !pixiModuleRef.current) return;

    const PIXI = pixiModuleRef.current;
    const app = appRef.current;
    const animator = animatorRef.current;

    // Clear
    app.stage.removeChildren();

    // Main container
    const main = new PIXI.Container();
    app.stage.addChild(main);

    // Reveal phase opacity multipliers
    const baseOpacity = revealPhase === 'dimmed' ? 0.5 : 1.0;
    const hubOpacity = revealPhase === 'complete' ? 1.0 : (revealPhase === 'revealing' ? 0.9 : 0.6);

    // Draw zone backgrounds based on actual node positions
    const zoneGraphics = new PIXI.Graphics();
    const zonePadding = 30;
    const zoneY = 40;
    const zoneHeight = dimensions.height - 80;

    // Group nodes by column to find column boundaries
    const nodesByCol = new Map<number, LayoutNode[]>();
    layout.nodes.forEach((node) => {
      const col = node.column;
      if (!nodesByCol.has(col)) nodesByCol.set(col, []);
      nodesByCol.get(col)!.push(node);
    });

    layout.zones.forEach((zone) => {
      // Find min/max x for nodes in this zone's columns
      let minX = Infinity;
      let maxX = -Infinity;

      for (let col = zone.startCol; col <= zone.endCol; col++) {
        const colNodes = nodesByCol.get(col) || [];
        colNodes.forEach((node) => {
          minX = Math.min(minX, node.x - node.width / 2);
          maxX = Math.max(maxX, node.x + node.width / 2);
        });
      }

      if (minX === Infinity) return; // No nodes in this zone

      const zoneX = minX - zonePadding;
      const zoneWidth = maxX - minX + zonePadding * 2;

      zoneGraphics.roundRect(zoneX, zoneY, zoneWidth, zoneHeight, 20);
      zoneGraphics.fill({ color: zone.color, alpha: zone.alpha * baseOpacity });
    });
    main.addChild(zoneGraphics);

    // Draw static edges (base lines) - DATA_FLOW is deliberately muted
    const edgeGraphics = new PIXI.Graphics();
    layout.edges.forEach((edge) => {
      const style = EDGE_COLORS[edge.type] || EDGE_COLORS.DATA_FLOW;
      // DATA_FLOW edges are almost invisible (boring on purpose)
      const edgeAlpha = edge.type === 'DATA_FLOW' ? 0.15 : 0.25;
      const strokeWidth = edge.type === 'DATA_FLOW' ? 1 : 2;

      edgeGraphics.moveTo(edge.sourceX, edge.sourceY);
      edgeGraphics.lineTo(edge.targetX, edge.targetY);
      edgeGraphics.stroke({ width: strokeWidth, color: edge.color, alpha: edgeAlpha * baseOpacity });
    });
    main.addChild(edgeGraphics);

    // Set up animator
    animator.setEdges(layout.edges.map((e) => ({
      id: e.id,
      sourceX: e.sourceX,
      sourceY: e.sourceY,
      targetX: e.targetX,
      targetY: e.targetY,
      type: e.type,
    })));

    // Particle graphics container
    const particleGraphics = new PIXI.Graphics();
    particleGraphicsRef.current = particleGraphics;
    main.addChild(particleGraphics);

    // Draw nodes - glass-box with inner shadow, no visible borders
    const textElements: any[] = [];
    const nodeGraphicsMap = new Map<string, any>();

    layout.nodes.forEach((node) => {
      const g = new PIXI.Graphics();
      const { x, y, width, height, isHub } = node;
      const nodeOpacity = isHub ? hubOpacity : baseOpacity * 0.85;

      // Hub glow effect (enhanced during reveal)
      if (isHub) {
        const glowIntensity = revealPhase === 'complete' ? 1.0 : (revealPhase === 'revealing' ? 0.8 : 0.3);
        // Outer glow
        g.circle(x, y, width / 2 + 25);
        g.fill({ color: colors.hubGlow, alpha: 0.08 * glowIntensity });
        g.circle(x, y, width / 2 + 15);
        g.fill({ color: colors.hubGlow, alpha: 0.12 * glowIntensity });
        g.circle(x, y, width / 2 + 8);
        g.fill({ color: colors.hubGlow, alpha: 0.18 * glowIntensity });
      }

      // Glass-box node - NO visible border, just subtle inner shadow effect
      // Outer subtle shadow (simulates depth)
      g.roundRect(x - width / 2 + 2, y - height / 2 + 2, width, height, 12);
      g.fill({ color: 0x000000, alpha: 0.1 * nodeOpacity });

      // Main node body
      g.roundRect(x - width / 2, y - height / 2, width, height, 12);
      g.fill({ color: colors.nodeBackground, alpha: nodeOpacity * (isHub ? 0.95 : 0.8) });

      // Inner highlight (top edge) - creates glass depth
      g.roundRect(x - width / 2 + 1, y - height / 2 + 1, width - 2, height / 3, 12);
      g.fill({ color: 0xffffff, alpha: 0.03 * nodeOpacity });

      // Hub gets subtle border, others get none
      if (isHub) {
        g.roundRect(x - width / 2, y - height / 2, width, height, 12);
        g.stroke({ width: 1.5, color: colors.hubGlow, alpha: 0.4 * hubOpacity });
      }

      // Interactive
      g.eventMode = 'static';
      g.cursor = 'pointer';
      g.hitArea = new PIXI.Rectangle(x - width / 2, y - height / 2, width, height);

      nodeGraphicsMap.set(node.label, g);

      g.on('pointerover', () => {
        setHoveredNode(node.label);
        setHoveredPath(getNodePath(node.label));
      });
      g.on('pointerout', () => {
        setHoveredNode(null);
        setHoveredPath(null);
      });
      g.on('pointertap', () => {
        selectNode({
          id: node.id,
          label: node.label,
          type: node.type,
          properties: {
            description: node.description,
            semanticId: node.semanticId,
            ...node.extra, // Include parent_name, parent_semantic_id, etc.
          },
        });
        // Trigger spark on connected ACTION edges
        layout.edges
          .filter((e) => e.type === 'ACTION_EXECUTION' && (e.sourceX === x || e.targetX === x))
          .forEach((e) => animator.triggerSpark(e.id));
      });

      main.addChild(g);

      // Typography hierarchy: marketing name primary, technical secondary
      const scale = layout.scale;
      const baseFontSize = isHub ? 14 : 11;
      const fontSize = Math.max(baseFontSize * scale, 9);
      const subFontSize = Math.max(9 * scale, 7);

      const marketingName = node.marketingName;
      const technicalName = node.label;
      const hasMarketingName = marketingName && marketingName !== technicalName;

      // Primary label (marketing name if available, else technical)
      const primaryText = hasMarketingName ? marketingName : technicalName;
      const maxChars = Math.floor(22 * scale);
      const truncatedPrimary = primaryText.length > maxChars
        ? primaryText.slice(0, maxChars - 2) + '...'
        : primaryText;

      const primaryLabel = new PIXI.Text({
        text: truncatedPrimary,
        style: {
          fontSize,
          fill: colors.textPrimary,
          fontFamily: 'system-ui, sans-serif',
          fontWeight: isHub ? '600' : '500',
        },
      });
      primaryLabel.anchor.set(0.5);
      primaryLabel.x = x;
      primaryLabel.y = hasMarketingName ? y - 6 * scale : y;
      primaryLabel.alpha = nodeOpacity;
      textElements.push(primaryLabel);

      // Secondary label (technical name, smaller and muted) - only if different
      if (hasMarketingName) {
        const truncatedTech = technicalName.length > maxChars
          ? technicalName.slice(0, maxChars - 2) + '...'
          : technicalName;

        const techLabel = new PIXI.Text({
          text: truncatedTech,
          style: {
            fontSize: subFontSize,
            fill: colors.textMuted,
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 'normal',
          },
        });
        techLabel.anchor.set(0.5);
        techLabel.x = x;
        techLabel.y = y + 8 * scale;
        techLabel.alpha = nodeOpacity * 0.7;
        textElements.push(techLabel);
      }
    });

    // Add text on top
    textElements.forEach((t) => main.addChild(t));

    // Depth fog / vignette overlay (center crisp, edges faded)
    const vignette = new PIXI.Graphics();
    const { width: w, height: h } = dimensions;
    const centerX = w / 2;
    const centerY = h / 2;

    // Radial gradient approximation with concentric rectangles
    const vignetteSteps = 8;
    for (let i = vignetteSteps; i >= 0; i--) {
      const ratio = i / vignetteSteps;
      const alpha = ratio * ratio * 0.15; // quadratic falloff, max 15% opacity
      const inset = ratio * Math.min(w, h) * 0.3;

      vignette.rect(inset, inset, w - inset * 2, h - inset * 2);
      vignette.fill({ color: colors.background, alpha });
    }
    main.addChild(vignette);

    // Animation loop
    const animate = (ticker: any) => {
      animator.update(ticker.deltaTime);

      // Redraw particles
      const pg = particleGraphicsRef.current;
      if (!pg) return;

      pg.clear();

      animator.getParticles().forEach((particle) => {
        const pos = animator.getParticlePosition(particle);

        // Glow effect
        pg.circle(pos.x, pos.y, particle.size * 2);
        pg.fill({ color: particle.color, alpha: particle.alpha * 0.2 });

        // Core
        pg.circle(pos.x, pos.y, particle.size);
        pg.fill({ color: particle.color, alpha: particle.alpha * 0.8 });

        // Bright center
        pg.circle(pos.x, pos.y, particle.size * 0.4);
        pg.fill({ color: 0xffffff, alpha: particle.alpha * 0.9 });
      });
    };

    app.ticker.add(animate);

    // Capture reference for cleanup
    const currentApp = app;
    const currentAnimate = animate;

    return () => {
      if (currentApp?.ticker) {
        currentApp.ticker.remove(currentAnimate);
      }
    };
  }, [pixiReady, layout, colors, selectNode, dimensions, revealPhase, getNodePath]);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading flow visualization...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <Network className="w-12 h-12 mb-4" />
        <p>Failed to load</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative overflow-hidden">
      <div ref={containerCallback} className="absolute inset-0 overflow-hidden">
        <div
          ref={pixiContainerCallback}
          className="absolute inset-0 overflow-hidden [&>canvas]:!w-full [&>canvas]:!h-full"
        />
      </div>

      {!pixiReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-10 flex flex-col gap-1.5 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-2.5 text-[10px]">
        <div className="text-[9px] uppercase tracking-wide text-muted-foreground mb-1">Flow Types</div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-slate-400" />
          <span className="text-muted-foreground">Data Flow</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-muted-foreground">Action Spark</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-cyan-400" />
          <span className="text-muted-foreground">Context Flow</span>
        </div>
      </div>

      {/* Stats */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center gap-3 bg-card/95 backdrop-blur-sm rounded-lg border border-border px-3 py-2 text-[10px] text-muted-foreground">
        <span>Pixi.js WebGL</span>
        <span className="text-border">|</span>
        <span>{data?.metadata.node_count || 0} nodes</span>
        <span className="text-border">|</span>
        <span>{data?.metadata.edge_count || 0} flows</span>
      </div>

      {/* Status narration bar - shows path context on hover */}
      {hoveredPath && (
        <div className="absolute top-3 left-3 z-10 px-3 py-2 bg-card/95 backdrop-blur-sm rounded-lg border border-border">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Data Path</div>
          <div className="text-sm font-medium text-foreground/90">{hoveredPath}</div>
        </div>
      )}

      {/* Title */}
      <div className="absolute top-3 right-3 z-10 text-right">
        <div className="text-sm font-medium text-foreground/80">Enterprise Integration Flow</div>
        <div className="text-[10px] text-muted-foreground">System-to-system data movement</div>
      </div>
    </div>
  );
}
